/**
 * THE ONE WRITER. Every voxel the terrain lays down goes through this loop, at every level.
 *
 * There used to be two of them — `generateChunk` and `generateChunkLOD` — plus two more transcriptions
 * of the same predicates in `generateLodColumn` and `sampleSurface`. They agreed by hand, which is to
 * say they did not: a thin feature point-sampled on one path and max-downsampled on another, a clip
 * that believed a different tower height from the draw, a waterline that sat at five heights across
 * five rings. See ColumnProfile's note for the shape that replaces them.
 *
 * THE STRIDE IS THE ONLY PARAMETER, and everything that used to be an `if (lod === 0)` is now a
 * quantity derived from it:
 *
 *   - weight is `coarseWeight(d, stride)`, which at stride 1 is exactly LOD 0's `clamp(d * 0.5)`;
 *   - the skin band and the road's paving are floored at one coarse voxel, `max(base, stride)` —
 *     already the rule in five places, because a feature thinner than a coarse row appears only
 *     where the terrain's remainder falls the right way, and now the rule rather than five copies;
 *   - the waterline is solved against the 4-bit pack at every level, which reduces exactly to the
 *     old saturated -0.5 at stride 1 (see solveWaterCrossing);
 *   - the air-column early-out is `top <= chunkFineY - stride`.
 *
 * ONE PER-VOXEL CALLBACK, and one only: `strata`, which is the same `getMaterialAtDepth` call the
 * old loops made. Everything else a voxel needs is column-constant and lives on the profile. That is
 * not fastidiousness — `voxel/surfaceBand.ts` states the rule and the reason: a per-voxel callback
 * here is a megamorphic call in the hottest code in the project.
 */
import { CHUNK_SIZE } from '../../voxel/constants.js';
import { coarseWeight, packVoxel } from '../../voxel/voxelData.js';
import { SOLID_WEIGHT_FLOOR, extrudedWeight } from '../../voxel/surfaceBand.js';
import { airAboveWaterWeight } from './ColumnProfile.js';
/** Depth (voxels) of the landform surface skin that gets sand/snow/gravel; below it, rock strata. */
export const LANDFORM_SKIN_DEPTH = 3;
/** Depth (fine voxels) of the city road's paving, before the coarse floor. */
export const ROAD_SKIN_DEPTH = 2;
/**
 * Write one column of a chunk from its profile. `chunkFineY` is the chunk's lowest sample in fine
 * voxels; `lx`/`lz` are its cell in the 32x32 slab.
 *
 * Returns false when the column is entirely air and nothing was written — the caller has already
 * zeroed the array, and skipping is what makes a sky chunk free.
 */
export function writeColumn(data, chunkFineY, lx, lz, p, ctx) {
    const stride = p.strideFine;
    if (p.top <= chunkFineY - stride)
        return false;
    // HOISTED, not read off the profile per voxel. Every one of these is column-constant, and the Y
    // loop is the hottest code in the project — the perf guide's invariant 7 in its plainest form.
    const height = p.height;
    const water = p.water;
    const waterMat = p.waterMat;
    const dotTop = p.dotTop;
    const dotMat = p.dotMat;
    const surfaceMat = p.surfaceMat;
    const exCount = p.exCount;
    const { exDist, exTop, exMat } = p;
    const { strataDepth, strataMat } = ctx;
    const strataCount = strataDepth.length;
    const deepMat = ctx.deepMaterial;
    const skinDepth = ctx.skinDepthFine > 0 ? ctx.skinDepthFine * stride : 0;
    // The drop to the lowest neighbour rides on top of both paving bands, so a steep riser's face
    // is paved all the way down to the neighbouring tread (see ColumnProfile.pavingDropFine). Riding
    // ON TOP (not a max) keeps one stride of slack under the riser for the surface's own rounding.
    const roadDepth = p.onRoad ? Math.max(ROAD_SKIN_DEPTH, stride) + p.pavingDropFine : -1;
    const roadMat = ctx.roadMaterial;
    const pathDepth = p.pathMat >= 0 ? Math.max(ROAD_SKIN_DEPTH, stride) + p.pavingDropFine : -1;
    const pathMat = p.pathMat;
    const hasWater = water > -Infinity;
    const waterFloorY = p.waterFloorFineY;
    const crossAirY = p.crossAirFineY;
    for (let ly = 0; ly < CHUNK_SIZE; ly++) {
        const fineY = chunkFineY + ly * stride;
        const distanceFromSurface = height - fineY; // fine voxels; positive = inside terrain
        let weight = coarseWeight(distanceFromSurface, stride);
        let material = 0;
        if (weight > -0.5) {
            const depth = distanceFromSurface > 0 ? distanceFromSurface : 0;
            material = deepMat;
            for (let i = 0; i < strataCount; i++) {
                if (depth <= strataDepth[i]) {
                    material = strataMat[i];
                    break;
                }
            }
            // The landform skin: sand / snow / gravel by elevation on the top few voxels; the rock strata
            // underneath stay from `strata`. Floored at one coarse voxel, or from lod 2 up the band is
            // thinner than a single sample and the surface reads as rock where grass should be.
            if (skinDepth > 0 && depth <= skinDepth)
                material = surfaceMat;
            // The city carriageway, then the path's paving over it, floored the same way and for the
            // same reason. The path wins where both claim a voxel: a track crossing a city keeps reading
            // as the track, at every LOD.
            if (depth <= roadDepth)
                material = roadMat;
            if (depth <= pathDepth)
                material = pathMat;
        }
        // Water fills between the terrain top and the water level.
        if (hasWater && weight < 0.5) {
            // waterFloorY: the one seabed sample a sheet thinner than the stride would otherwise miss.
            if (fineY <= water && (fineY > height || fineY === waterFloorY)) {
                weight = coarseWeight(water - fineY, stride);
                material = waterMat;
            }
            else if (fineY > water && height < water && (weight <= -0.5 || fineY === crossAirY)) {
                // THE AIR ABOVE THE WATER CARRIES THE WATER'S DISTANCE, NOT A SATURATED -0.5. The surface is
                // the zero crossing between two samples; pin the air side and the crossing is pulled down
                // toward the last water voxel, by more the coarser the level, so every ring puts the sea at
                // a different height and every fine|coarse border is a step. The material stays air — a
                // negative weight is outside for every mesh type, so only the crossing moves.
                weight = airAboveWaterWeight(p, fineY);
            }
        }
        // The per-column tree speckle, above the level where individual stand-ins pay for themselves.
        if (fineY > height && fineY <= dotTop) {
            weight = coarseWeight(dotTop - fineY, stride);
            material = dotMat;
        }
        // Extrusions, in precedence order, MERGED BY MAX — the no-carve rule stated numerically. A max
        // cannot lower a voxel, so it cannot erode the terrain or the water it crosses, and it cannot
        // depend on draw order. Material follows only where the result is actually solid, or the band
        // around a wall would recolour the air.
        if (fineY > height) { // extrusions stand ON the ground, never in it
            for (let i = 0; i < exCount; i++) {
                const w = extrudedWeight(exDist[i], -Infinity, exTop[i], fineY, stride);
                if (w > weight)
                    weight = w;
                if (w >= SOLID_WEIGHT_FLOOR)
                    material = exMat[i];
            }
        }
        data[lx + ly * CHUNK_SIZE + lz * CHUNK_SIZE * CHUNK_SIZE] = packVoxel(weight, material, 0);
    }
    return true;
}
//# sourceMappingURL=ColumnPipeline.js.map